import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../components/AuthProvider';
import { useNotification } from '../../components/member/NotificationProvider';
import { supabase } from '../../lib/supabase';
import { getReceivedOrders, getMyTournaments, getMyProductsCount, getMyStore, updateStoreSalesStatus, cleanupExpiredOrders } from '../../services/supabaseService';
import { ChangelogWidget } from '../../components/ChangelogWidget';

export const LojistaDashboard: React.FC = () => {
    const { user } = useAuth();
    const { showNotification } = useNotification();
    const [allOrders, setAllOrders] = useState<any[]>([]);
    const [recentOrders, setRecentOrders] = useState<any[]>([]);
    const [allTournaments, setAllTournaments] = useState<any[]>([]);
    const [productsCount, setProductsCount] = useState<number>(0);
    const [store, setStore] = useState<any>(null);
    const [togglingSales, setTogglingSales] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            try {
                // Atualiza pedidos expirados para manter os dados sempre sincronizados
                try {
                    await cleanupExpiredOrders();
                } catch (e) {
                    console.warn('cleanupExpiredOrders error in dashboard:', e);
                }

                const myStore = await getMyStore();
                const storeId = myStore?.id;

                const [orders, myTournaments, count] = await Promise.all([
                    getReceivedOrders(storeId),
                    getMyTournaments(storeId),
                    getMyProductsCount(storeId)
                ]);

                const fullOrdersList = orders || [];
                const fullTourneysList = myTournaments || [];

                setAllOrders(fullOrdersList);
                setAllTournaments(fullTourneysList);
                setProductsCount(count || 0);
                setStore(myStore);

                const topOrders = fullOrdersList.slice(0, 15);

                // Garante hidratação de quem fez o pedido via RPC get_safe_users
                const missingBuyerIds = topOrders
                    .filter(o => o.buyer_id && (!o.buyer || !o.buyer.username))
                    .map(o => Number(o.buyer_id))
                    .filter(id => !isNaN(id));

                if (missingBuyerIds.length > 0) {
                    try {
                        const { data: safeUsers } = await (supabase.rpc('get_safe_users') as any)
                            .select('id, username, fullname, avatar')
                            .in('id', missingBuyerIds);

                        if (safeUsers && safeUsers.length > 0) {
                            const userMap = new Map();
                            safeUsers.forEach((u: any) => {
                                userMap.set(u.id, u);
                                userMap.set(Number(u.id), u);
                                userMap.set(String(u.id), u);
                            });
                            topOrders.forEach(o => {
                                const matchedUser = userMap.get(o.buyer_id) || userMap.get(Number(o.buyer_id));
                                if (matchedUser) {
                                    o.buyer = { ...(o.buyer || {}), ...matchedUser };
                                }
                            });
                        }
                    } catch (safeErr) {
                        console.warn('Erro ao carregar dados seguros dos compradores via get_safe_users:', safeErr);
                    }
                }

                setRecentOrders(topOrders);
            } catch (error) {
                console.error("Error fetching lojista stats:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [user?.id]);

    const handleToggleSales = async () => {
        if (!store) return;
        const nextStatus = !store.sales_enabled;
        setTogglingSales(true);
        try {
            const success = await updateStoreSalesStatus(store.id, nextStatus);
            if (success) {
                setStore((prev: any) => prev ? { ...prev, sales_enabled: nextStatus } : null);
                showNotification(
                    nextStatus
                        ? "Vendas ativadas na loja! Seus produtos já podem ser comprados."
                        : "Vendas desativadas! Sua loja foi pausada para novos pedidos.",
                    "success"
                );
            } else {
                showNotification("Não foi possível alterar o status das vendas.", "error");
            }
        } catch (err) {
            console.error("Error toggling sales:", err);
            showNotification("Erro ao alterar status das vendas", "error");
        } finally {
            setTogglingSales(false);
        }
    };

    const stats = useMemo(() => {
        // Pedidos pendentes de pagamento em toda a loja
        const pendingCount = allOrders.filter(o => 
            ['pending', 'pending_payment', 'waiting_payment'].includes(o.status?.toLowerCase())
        ).length;

        // Vendas confirmadas/pagas (pedidos pagos, em preparação, enviados ou entregues)
        const paidOrders = allOrders.filter(o => 
            ['paid', 'processing', 'shipped', 'delivered'].includes(o.status?.toLowerCase())
        );
        const salesCount = paidOrders.length;
        const totalRevenue = paidOrders.reduce((sum, o) => sum + (Number(o.amount) || 0), 0);

        // Torneios atualmente ativos (abertos ou em andamento)
        const activeTournamentsCount = allTournaments.filter(t => 
            !['finished', 'cancelled', 'canceled'].includes(t.status?.toLowerCase())
        ).length;

        return [
            { 
                label: 'Pedidos Pendentes', 
                value: pendingCount, 
                icon: 'fa-clock', 
                color: 'text-yellow-500' 
            },
            { 
                label: 'Vendas Totais', 
                value: salesCount, 
                icon: 'fa-cart-shopping', 
                color: 'text-emerald-500' 
            },
            { 
                label: 'Torneios Ativos', 
                value: activeTournamentsCount, 
                icon: 'fa-trophy', 
                color: 'text-purple-500' 
            },
            { 
                label: 'Total de Produtos', 
                value: productsCount, 
                icon: 'fa-boxes-stacked', 
                color: 'text-blue-500' 
            },
        ];
    }, [allOrders, allTournaments, productsCount]);

    return (
        <div className="space-y-6 pb-10">
            <header className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-white tracking-tight uppercase">Painel de Controle</h1>
                </div>
            </header>

            {/* Store Sales Status Banner */}
            {store && (
                <div className={`p-4 rounded-xl border flex flex-col sm:flex-row sm:items-center justify-between gap-4 ${
                    store.sales_enabled 
                        ? 'bg-emerald-950/20 border-emerald-500/30' 
                        : 'bg-amber-950/25 border-amber-500/30'
                }`}>
                    <div className="flex items-center gap-3.5">
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                            store.sales_enabled 
                                ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' 
                                : 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                        }`}>
                            <i className={`fas ${store.sales_enabled ? 'fa-store' : 'fa-store-slash'} text-sm`}></i>
                        </div>
                        <div>
                            <div className="flex items-center gap-2">
                                <span className={`w-2 h-2 rounded-full shrink-0 ${store.sales_enabled ? 'bg-emerald-400' : 'bg-amber-400'}`}></span>
                                <h3 className="text-xs font-bold text-white uppercase tracking-tight">
                                    Vendas na Loja: {store.sales_enabled ? (
                                        <span className="text-emerald-400">Ativadas</span>
                                    ) : (
                                        <span className="text-amber-400">Desativadas (Pausadas)</span>
                                    )}
                                </h3>
                            </div>
                            <p className="text-xs text-slate-400 mt-0.5">
                                {store.sales_enabled 
                                    ? 'Sua loja está aceitando novos pedidos normalmente no Cardumy.' 
                                    : 'Sua loja está no modo catálogo. Novos pedidos e adições ao carrinho estão bloqueados.'}
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center gap-3 self-end sm:self-center">
                        <button
                            type="button"
                            disabled={togglingSales}
                            onClick={handleToggleSales}
                            className={`px-3.5 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider transition-colors flex items-center gap-2 disabled:opacity-50 ${
                                store.sales_enabled
                                    ? 'bg-slate-800 hover:bg-red-500/20 text-slate-300 hover:text-red-400 border border-slate-700'
                                    : 'bg-emerald-600 hover:bg-emerald-500 text-white'
                            }`}
                        >
                            {togglingSales ? (
                                <>
                                    <i className="fas fa-spinner fa-spin"></i>
                                    <span>Salvando...</span>
                                </>
                            ) : store.sales_enabled ? (
                                <>
                                    <i className="fas fa-pause"></i>
                                    <span>Pausar Vendas</span>
                                </>
                            ) : (
                                <>
                                    <i className="fas fa-play"></i>
                                    <span>Ativar Vendas</span>
                                </>
                            )}
                        </button>
                        <Link 
                            to="/minha-loja" 
                            className="p-1.5 text-slate-500 hover:text-white transition-colors"
                            title="Configurações da Loja"
                        >
                            <i className="fas fa-gear text-sm"></i>
                        </Link>
                    </div>
                </div>
            )}

            {/* Stats Grid */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {stats.map((stat, i) => (
                    <div key={i} className="bg-slate-900 border border-slate-800 p-4 rounded-xl flex flex-col items-center text-center">
                        <div className={`w-9 h-9 rounded-lg bg-slate-800 flex items-center justify-center ${stat.color} mb-2`}>
                            <i className={`fas ${stat.icon}`}></i>
                        </div>
                        <span className="text-xl font-bold text-white">{stat.value}</span>
                        <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mt-1">{stat.label}</span>
                    </div>
                ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
                {/* Recent Orders */}
                <section className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden flex flex-col h-full">
                    <div className="px-6 py-5 border-b border-slate-800 flex items-center justify-between shrink-0">
                        <div className="flex items-center gap-2">
                            <i className="fa-solid fa-receipt text-purple-400 text-sm"></i>
                            <h2 className="text-sm font-black text-white uppercase tracking-widest">Pedidos Recentes</h2>
                        </div>
                        <Link to="/pedidos-recebidos" className="text-[11px] font-bold text-purple-400 hover:text-purple-300 transition-colors uppercase tracking-wider flex items-center gap-1">
                            Ver todos
                            <i className="fa-solid fa-arrow-right text-[10px]"></i>
                        </Link>
                    </div>
                    <div className="p-2 divide-y divide-slate-800/40 flex-1 overflow-y-auto">
                        {loading ? (
                            <div className="p-8 text-center text-slate-500 animate-pulse font-bold uppercase text-xs flex items-center justify-center gap-2">
                                <i className="fa-solid fa-spinner fa-spin text-purple-500"></i>
                                Carregando pedidos...
                            </div>
                        ) : recentOrders.length > 0 ? (
                            recentOrders.map((order, i) => {
                                const buyerName = order.buyer?.fullname || (order.buyer?.username ? `@${order.buyer.username}` : 'Cliente');
                                const buyerUsername = order.buyer?.username;
                                const initials = buyerUsername 
                                    ? buyerUsername.slice(0, 2).toUpperCase() 
                                    : (order.buyer?.fullname ? order.buyer.fullname.slice(0, 2).toUpperCase() : 'CL');

                                return (
                                    <Link 
                                        key={order.id || i}
                                        to={`/pedidos-recebidos/${order.id}`}
                                        className="flex items-center justify-between p-3.5 hover:bg-slate-800/50 rounded-xl transition-all group block"
                                    >
                                        <div className="flex items-center space-x-3.5 min-w-0">
                                            {/* Avatar do Comprador com fallback para iniciais */}
                                            <div className="relative shrink-0">
                                                {order.buyer?.avatar ? (
                                                    <img 
                                                        src={order.buyer.avatar} 
                                                        alt={buyerName} 
                                                        className="w-10 h-10 rounded-full object-cover border border-slate-700/80 bg-slate-800 group-hover:border-purple-500/50 transition-colors"
                                                        onError={(e) => {
                                                            (e.target as HTMLElement).style.display = 'none';
                                                            const fallback = (e.target as HTMLElement).nextElementSibling as HTMLElement;
                                                            if (fallback) fallback.style.display = 'flex';
                                                        }}
                                                    />
                                                ) : null}
                                                <div 
                                                    className={`w-10 h-10 rounded-full bg-slate-800 border border-slate-700/80 flex items-center justify-center text-purple-300 font-black text-xs ${order.buyer?.avatar ? 'hidden' : 'flex'}`}
                                                >
                                                    {initials}
                                                </div>
                                            </div>

                                            {/* Identificação de quem realizou a compra */}
                                            <div className="min-w-0">
                                                <div className="flex items-center gap-1.5 flex-wrap">
                                                    <p className="text-sm font-bold text-white group-hover:text-purple-400 transition-colors truncate max-w-[140px] sm:max-w-[200px]">
                                                        {buyerName}
                                                    </p>
                                                    {buyerUsername && order.buyer?.fullname && (
                                                        <span className="text-[11px] text-slate-400 font-medium truncate max-w-[110px]">
                                                            @{buyerUsername}
                                                        </span>
                                                    )}
                                                </div>

                                                <div className="flex items-center gap-1.5 text-[10px] text-slate-400 font-bold uppercase tracking-tight mt-0.5">
                                                    <span className="font-mono text-purple-400">
                                                        #{order.external_reference ? order.external_reference.slice(-7).toUpperCase() : order.id.toString().slice(-7).toUpperCase()}
                                                    </span>
                                                    <span className="text-slate-600">•</span>
                                                    <span className="text-slate-200">R$ {order.amount?.toFixed(2)}</span>
                                                    <span className="text-slate-600">•</span>
                                                    <span className="text-slate-500">{new Date(order.created_at).toLocaleDateString()}</span>
                                                </div>
                                            </div>
                                        </div>

                                        <span className={`text-[9px] font-black uppercase px-2.5 py-1 rounded-full shrink-0 ml-2 border ${
                                            order.status === 'paid' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' :
                                            (order.status === 'pending' || order.status === 'pending_payment' || order.status === 'waiting_payment') ? 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20' :
                                            order.status === 'expired' ? 'bg-red-500/10 text-red-400 border-red-500/20' :
                                            (order.status === 'canceled' || order.status === 'cancelled') ? 'bg-rose-500/10 text-rose-400 border-rose-500/20' :
                                            'bg-slate-800 text-slate-400 border-slate-700'
                                        }`}>
                                            {order.status === 'paid' ? 'Pago' :
                                             (order.status === 'pending' || order.status === 'pending_payment' || order.status === 'waiting_payment') ? 'Pendente' :
                                             order.status === 'expired' ? 'Expirado' :
                                             (order.status === 'canceled' || order.status === 'cancelled') ? 'Cancelado' : order.status}
                                        </span>
                                    </Link>
                                );
                            })
                        ) : (
                            <div className="p-12 text-center text-slate-500 italic text-sm">Nenhum pedido recente.</div>
                        )}
                    </div>
                </section>

                {/* Quick Management */}
                <section className="space-y-4 sm:space-y-6">
                    <h2 className="text-sm font-black text-white uppercase tracking-widest px-1">Ações Rápidas</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                        <Link 
                            to="/gerenciar-estoque" 
                            className="group bg-slate-900/40 hover:bg-slate-900/70 border border-slate-800 hover:border-purple-500/40 p-4 sm:p-5 rounded-2xl transition-all flex flex-col justify-between"
                        >
                            <div>
                                <div className="w-10 h-10 rounded-xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center text-purple-400 mb-3 group-hover:scale-110 transition-transform">
                                    <i className="fas fa-boxes-stacked text-base"></i>
                                </div>
                                <h3 className="font-bold text-sm text-white group-hover:text-purple-400 transition-colors tracking-tight">Vitrine & Estoque</h3>
                                <p className="text-xs text-slate-400 mt-1 leading-relaxed">Atualize cartas e produtos disponíveis para venda.</p>
                            </div>
                            <div className="mt-4 pt-3 border-t border-slate-800/60 flex items-center justify-between text-[11px] font-bold text-slate-500 group-hover:text-purple-400 transition-colors">
                                <span>Acessar vitrine</span>
                                <i className="fas fa-arrow-right text-[10px] group-hover:translate-x-1 transition-transform"></i>
                            </div>
                        </Link>

                        <Link 
                            to="/gerenciar-inventario" 
                            className="group bg-slate-900/40 hover:bg-slate-900/70 border border-slate-800 hover:border-cyan-500/40 p-4 sm:p-5 rounded-2xl transition-all flex flex-col justify-between"
                        >
                            <div>
                                <div className="w-10 h-10 rounded-xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center text-cyan-400 mb-3 group-hover:scale-110 transition-transform">
                                    <i className="fas fa-boxes-packing text-base"></i>
                                </div>
                                <h3 className="font-bold text-sm text-white group-hover:text-cyan-400 transition-colors tracking-tight">Almoxarifado</h3>
                                <p className="text-xs text-slate-400 mt-1 leading-relaxed">Insumos, embalagens, premiações e consumo interno.</p>
                            </div>
                            <div className="mt-4 pt-3 border-t border-slate-800/60 flex items-center justify-between text-[11px] font-bold text-slate-500 group-hover:text-cyan-400 transition-colors">
                                <span>Acessar inventário</span>
                                <i className="fas fa-arrow-right text-[10px] group-hover:translate-x-1 transition-transform"></i>
                            </div>
                        </Link>

                        <Link 
                            to="/gerenciar-creditos" 
                            className="group bg-slate-900/40 hover:bg-slate-900/70 border border-slate-800 hover:border-emerald-500/40 p-4 sm:p-5 rounded-2xl transition-all flex flex-col justify-between"
                        >
                            <div>
                                <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400 mb-3 group-hover:scale-110 transition-transform">
                                    <i className="fas fa-coins text-base"></i>
                                </div>
                                <h3 className="font-bold text-sm text-white group-hover:text-emerald-400 transition-colors tracking-tight">Créditos de Clientes</h3>
                                <p className="text-xs text-slate-400 mt-1 leading-relaxed">Saldos, premiações e resgates de crédito em loja.</p>
                            </div>
                            <div className="mt-4 pt-3 border-t border-slate-800/60 flex items-center justify-between text-[11px] font-bold text-slate-500 group-hover:text-emerald-400 transition-colors">
                                <span>Gerir créditos</span>
                                <i className="fas fa-arrow-right text-[10px] group-hover:translate-x-1 transition-transform"></i>
                            </div>
                        </Link>

                        <Link 
                            to="/meus-torneios" 
                            className="group bg-slate-900/40 hover:bg-slate-900/70 border border-slate-800 hover:border-amber-500/40 p-4 sm:p-5 rounded-2xl transition-all flex flex-col justify-between"
                        >
                            <div>
                                <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-400 mb-3 group-hover:scale-110 transition-transform">
                                    <i className="fas fa-trophy text-base"></i>
                                </div>
                                <h3 className="font-bold text-sm text-white group-hover:text-amber-400 transition-colors tracking-tight">Torneios</h3>
                                <p className="text-xs text-slate-400 mt-1 leading-relaxed">Gerencie eventos, inscrições e chaveamentos.</p>
                            </div>
                            <div className="mt-4 pt-3 border-t border-slate-800/60 flex items-center justify-between text-[11px] font-bold text-slate-500 group-hover:text-amber-400 transition-colors">
                                <span>Ver torneios</span>
                                <i className="fas fa-arrow-right text-[10px] group-hover:translate-x-1 transition-transform"></i>
                            </div>
                        </Link>

                        <Link 
                            to="/minha-loja/mercadopago" 
                            className="group bg-slate-900/40 hover:bg-slate-900/70 border border-slate-800 hover:border-blue-500/40 p-4 sm:p-5 rounded-2xl transition-all flex flex-col justify-between"
                        >
                            <div>
                                <div className="w-10 h-10 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center text-blue-400 mb-3 group-hover:scale-110 transition-transform">
                                    <i className="fas fa-wallet text-base"></i>
                                </div>
                                <h3 className="font-bold text-sm text-white group-hover:text-blue-400 transition-colors tracking-tight">Mercado Pago</h3>
                                <p className="text-xs text-slate-400 mt-1 leading-relaxed">Configure chaves de Pix e recebimento online.</p>
                            </div>
                            <div className="mt-4 pt-3 border-t border-slate-800/60 flex items-center justify-between text-[11px] font-bold text-slate-500 group-hover:text-blue-400 transition-colors">
                                <span>Configurar pagamentos</span>
                                <i className="fas fa-arrow-right text-[10px] group-hover:translate-x-1 transition-transform"></i>
                            </div>
                        </Link>
                        
                        <Link 
                            to="/minha-loja/upload" 
                            className="group bg-slate-900/40 hover:bg-slate-900/70 border border-slate-800 hover:border-purple-500/40 p-4 sm:p-5 rounded-2xl transition-all flex flex-col justify-between"
                        >
                            <div>
                                <div className="w-10 h-10 rounded-xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center text-purple-400 mb-3 group-hover:scale-110 transition-transform">
                                    <i className="fas fa-cloud-arrow-up text-base"></i>
                                </div>
                                <h3 className="font-bold text-sm text-white group-hover:text-purple-400 transition-colors tracking-tight">Upload de Imagens</h3>
                                <p className="text-xs text-slate-400 mt-1 leading-relaxed">Hospede fotos de produtos, logos e banners no Cloudinary.</p>
                            </div>
                            <div className="mt-4 pt-3 border-t border-slate-800/60 flex items-center justify-between text-[11px] font-bold text-slate-500 group-hover:text-purple-400 transition-colors">
                                <span>Fazer upload</span>
                                <i className="fas fa-arrow-right text-[10px] group-hover:translate-x-1 transition-transform"></i>
                            </div>
                        </Link>

                        <Link 
                            to="/minha-loja" 
                            className="group bg-slate-900/40 hover:bg-slate-900/70 border border-slate-800 hover:border-slate-500/40 p-4 sm:p-5 rounded-2xl transition-all flex flex-col justify-between"
                        >
                            <div>
                                <div className="w-10 h-10 rounded-xl bg-slate-700/30 border border-slate-700/50 flex items-center justify-center text-slate-300 mb-3 group-hover:scale-110 transition-transform">
                                    <i className="fas fa-store text-base"></i>
                                </div>
                                <h3 className="font-bold text-sm text-white group-hover:text-slate-300 transition-colors tracking-tight">Configurações</h3>
                                <p className="text-xs text-slate-400 mt-1 leading-relaxed">Horários, dados, fretes e perfil da loja.</p>
                            </div>
                            <div className="mt-4 pt-3 border-t border-slate-800/60 flex items-center justify-between text-[11px] font-bold text-slate-500 group-hover:text-slate-300 transition-colors">
                                <span>Ajustar loja</span>
                                <i className="fas fa-arrow-right text-[10px] group-hover:translate-x-1 transition-transform"></i>
                            </div>
                        </Link>
                    </div>

                    <div className="bg-gradient-to-br from-slate-900 to-purple-950/40 border border-slate-800 p-5 sm:p-6 rounded-2xl relative overflow-hidden group">
                        <div className="relative z-10 flex items-center justify-between">
                            <div>
                                <h3 className="text-base font-black text-white uppercase tracking-tight">Precisa de Ajuda?</h3>
                                <p className="text-xs text-slate-400 mt-1 max-w-xs leading-relaxed">Fale com nosso time de suporte para tirar dúvidas e otimizar suas vendas.</p>
                                <Link to="/suporte" className="inline-flex items-center mt-3 text-[11px] font-black text-purple-400 uppercase tracking-widest hover:text-white transition-colors">
                                    Abrir Chamado <i className="fas fa-arrow-right ml-2 text-[9px]"></i>
                                </Link>
                            </div>
                            <div className="w-12 h-12 rounded-2xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center text-purple-400/80 group-hover:scale-110 group-hover:text-purple-400 transition-all shrink-0 ml-4">
                                <i className="fas fa-headset text-xl"></i>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </div>
    );
};
